/**
 * custom-ajax-cart.js
 * ------------------------------------------------------------------
 * Makes custom AJAX add-to-cart refresh the cart UI by reusing Horizon's
 * native event system instead of duplicating drawer logic.
 *
 * Horizon's cart UI listens on `document` for a single event, "cart:update"
 * (ThemeEvents.cartUpdate). When it fires:
 *   - cart-icon            updates the count bubble (detail.data.itemCount)
 *   - cart-items-component refreshes the drawer/cart (morphs detail.data.sections,
 *                          or re-fetches itself if sections are absent)
 *   - cart-drawer-component auto-opens (and exposes a native .open())
 *
 * This file provides:
 *   1. refreshCartUI()     - read /cart.js and broadcast cart:update (+ optional open)
 *   2. customAddToCart()   - add + refresh, for any new buttons you build
 *   3. a [data-custom-add-to-cart] click helper
 *   4. a scoped bridge for the existing custom product page (pp* functions)
 *
 * Framework-agnostic (no @theme imports); safe as a classic deferred script.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CART_UPDATE_EVENT = 'cart:update';
  // Not 'product-form-component' -> cart-icon SETS the bubble to itemCount
  // (the absolute total) rather than adding a delta.
  var SOURCE = 'custom-add-to-cart';
  var inFlight = new WeakSet();

  function cartAddUrl() {
    return (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
  }

  function getCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids.join(',');
  }

  /**
   * Read the fresh cart and broadcast Horizon's native cart:update event, so the
   * count bubble and the cart drawer/cart-items refresh themselves.
   * @param {boolean} openDrawer - open the drawer via its native method afterwards
   * @param {Object} [sections]  - pre-rendered section HTML (from /cart/add.js)
   * @returns {Promise<Object|undefined>} the fresh cart
   */
  async function refreshCartUI(openDrawer, sections) {
    try {
      var cart = await (await fetch('/cart.js')).json();
      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: SOURCE,
            data: { source: SOURCE, itemCount: cart.item_count, sections: sections },
          },
        })
      );
      if (openDrawer) {
        var drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      }
      return cart;
    } catch (err) {
      console.error('[custom-ajax-cart] refresh failed:', err);
    }
  }
  window.refreshCartUI = refreshCartUI;

  function setLoading(trigger, isLoading) {
    if (!trigger) return;
    if (isLoading) {
      trigger.setAttribute('aria-busy', 'true');
      trigger.classList.add('is-loading');
      if ('disabled' in trigger) trigger.disabled = true;
    } else {
      trigger.removeAttribute('aria-busy');
      trigger.classList.remove('is-loading');
      if ('disabled' in trigger) trigger.disabled = false;
    }
  }

  /**
   * Add an item, then refresh via refreshCartUI(). For new buttons you build.
   * @param {Object} options {id, quantity, formData, trigger, productId, openDrawer}
   * @returns {Promise<Object|undefined>}
   */
  async function customAddToCart(options) {
    options = options || {};
    var trigger = options.trigger || null;
    var openDrawer = options.openDrawer !== false;

    if (trigger && inFlight.has(trigger)) return; // (5) block duplicate clicks
    if (trigger) inFlight.add(trigger);
    setLoading(trigger, true); // (6) loading state

    try {
      var sectionIds = getCartSectionIds();
      var fetchOptions;
      if (options.formData instanceof FormData) {
        options.formData.set('sections', sectionIds);
        options.formData.set('sections_url', window.location.pathname);
        fetchOptions = { method: 'POST', headers: { Accept: 'application/json' }, body: options.formData };
      } else {
        fetchOptions = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: options.id, quantity: options.quantity || 1 }],
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        };
      }

      var addRes = await fetch(cartAddUrl(), fetchOptions);
      var addJson = await addRes.json();

      if (!addRes.ok || addJson.status) {
        document.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              sourceId: SOURCE,
              data: { message: addJson.message, description: addJson.description, errors: addJson.errors },
            },
          })
        );
        return;
      }

      return await refreshCartUI(openDrawer, addJson.sections);
    } catch (err) {
      console.error('[custom-ajax-cart] add failed:', err);
    } finally {
      setLoading(trigger, false);
      if (trigger) inFlight.delete(trigger);
    }
  }
  window.customAddToCart = customAddToCart;

  // Convenience wiring for any element/form with [data-custom-add-to-cart].
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-custom-add-to-cart]');
    if (!trigger) return;
    event.preventDefault();
    var form = trigger.closest('form');
    if (form) {
      customAddToCart({ formData: new FormData(form), trigger: trigger });
    } else {
      customAddToCart({
        id: trigger.getAttribute('data-custom-add-to-cart'),
        quantity: parseInt(trigger.getAttribute('data-quantity') || '1', 10),
        trigger: trigger,
        productId: trigger.getAttribute('data-product-id') || undefined,
      });
    }
  });

  /**
   * Bridge for the existing custom product-page block (global functions prefixed
   * `pp`: ppAddToCart, ppShowUpsell, ppAddUpsell, ppAddRelated). They call
   * fetch('/cart/add.js') directly and never notify the theme. We wrap them so
   * that, WHILE one is running, the add request it fires is detected and followed
   * by a native cart refresh. The wrapper is scoped: `activeOpen` is only set
   * during a pp* call, so add-to-cart anywhere else is completely untouched.
   * This requires no edits to the product templates.
   */
  (function bridgeLegacyProductPage() {
    // Per-function: should the cart drawer open after adding?
    // Main add -> yes. Modal/redirect flows -> no (they show their own UI).
    var PP = { ppAddToCart: true, ppShowUpsell: false, ppAddUpsell: false, ppAddRelated: false };
    var activeOpen = null; // openDrawer for the currently-running pp fn, else null

    var origFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var promise = origFetch(input, init);
      try {
        if (activeOpen !== null) {
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
          if (method === 'POST' && /\/cart\/add(\.js)?($|\?)/.test(url)) {
            var openDrawer = activeOpen;
            promise.then(function () { refreshCartUI(openDrawer); });
          }
        }
      } catch (e) {
        /* no-op */
      }
      return promise;
    };

    function wrap(name) {
      var fn = window[name];
      if (typeof fn !== 'function' || fn.__ppWrapped) return;
      var openDrawer = PP[name];
      var wrapped = function () {
        var prev = activeOpen;
        activeOpen = openDrawer;
        try {
          return fn.apply(this, arguments);
        } finally {
          // The add fetch is fired synchronously inside fn, so it's already
          // captured; reset on the next task.
          setTimeout(function () { activeOpen = prev; }, 0);
        }
      };
      wrapped.__ppWrapped = true;
      window[name] = wrapped;
    }
    function wrapAll() { Object.keys(PP).forEach(wrap); }

    wrapAll(); // pp* are defined by an inline (parser) script before this deferred file runs
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wrapAll);
    window.addEventListener('load', wrapAll); // safety net; wrap() is idempotent
  })();
})();
